import { colorFields } from "./_shared";
/**
 * Shop the look — one styled photograph beside the individual products that
 * make it up, each linked. The seller picks the products explicitly; nothing
 * is inferred from a category.
 *
 * Ported from the EasyOrders `shop-the-look` section found on avyro/elite.
 * It was absent from the original section census entirely — the first proof
 * that the undercount hid whole SECTION TYPES, not just extra instances (see
 * docs/plans/2026-07-19-storefront-section-vocabulary.md).
 *
 * Their `--cream` / `--charcoal` tones are NOT modelled here: the shared
 * `surface` field already selects a band, and a second, section-local tone
 * control would compete with it.
 */
export const shopTheLookSchema = {
    type: "shop-the-look",
    name: { ar: "اشتري الإطلالة", en: "Shop the look" },
    description: {
        ar: "صورة إطلالة كاملة وجنبها المنتجات اللي فيها، كل واحد بلينك.",
        en: "One styled photo beside the linked products that make it up.",
    },
    maxPerTemplate: 3,
    availableOnTemplates: ["home"],
    settings: [
        {
            /**
             * Added 2026-07-20 with the prime family. The section shipped as a
             * single layout until then; `look-carousel` is prime's `stl__carousel`.
             *
             * The two are the same SECTION — a styled photograph and the products
             * inside it — laid out differently, so this is a variant rather than a
             * second type:
             *
             *  - `look-classic` (avyro/elite, the original): ONE look, its photo on
             *    one side and the product cards listed beside it.
             *  - `look-carousel` (prime): SEVERAL looks in a horizontal scroller,
             *    each a full-height image with a pin badge; the products of a look
             *    open in a dialog from its badge rather than sitting beside it.
             *
             * The carousel needs MANY looks where the classic layout has exactly
             * one, so it reads the `look` BLOCK list below instead of the
             * `heroImage` / `productIds` pair. The two field sets are scoped by
             * `showIf` so the inspector never shows both at once.
             *
             * MEASURED on prime-books: slides are a fixed 515px tall at >=900 and
             * 345px below, each image `width: auto` at its natural ratio (so the
             * slides are deliberately UNEQUAL widths — 687/426/517/773 at 1440),
             * track gap `clamp(12px, 2vw, 20px)`, slide radius 10px. The section is
             * 717px at 1440 and 516px at 390.
             */
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "إطلالة واحدة جنب منتجاتها، ولا كذا إطلالة في شريط.",
                en: "One look beside its products, or several looks in a scroller.",
            },
            options: [
                { value: "look-classic", label: { ar: "إطلالة واحدة", en: "Single look" } },
                { value: "look-carousel", label: { ar: "شريط إطلالات", en: "Look carousel" } },
            ],
            default: "look-classic",
        },
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "سطر فوق العنوان", en: "Eyebrow" },
            validate: { max: 40 },
            default: { ar: "غرفة التنسيق", en: "Styling room" },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 80 },
            default: { ar: "اشتري الإطلالة", en: "Shop the look" },
        },
        {
            id: "subtitle",
            type: "textarea",
            i18n: true,
            label: { ar: "الوصف", en: "Subtitle" },
            validate: { max: 160 },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        {
            id: "heroImage",
            type: "image",
            label: { ar: "صورة الإطلالة", en: "Look photo" },
            helpText: {
                ar: "صورة طولية للإطلالة كاملة.",
                en: "A tall photo of the complete outfit or scene.",
            },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "heroTitle",
            type: "text",
            i18n: true,
            label: { ar: "اسم الإطلالة", en: "Look name" },
            validate: { max: 60 },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "heroCaption",
            type: "textarea",
            i18n: true,
            label: { ar: "وصف الإطلالة", en: "Look caption" },
            validate: { max: 160 },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "heroCtaLabel",
            type: "text",
            i18n: true,
            label: { ar: "زر الإطلالة", en: "Look button" },
            validate: { max: 30 },
            default: { ar: "شوف الإطلالة", en: "Shop full look" },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "heroHref",
            type: "url",
            label: { ar: "لينك الإطلالة", en: "Look link" },
            helpText: {
                ar: "الصفحة اللي الصورة بتوديها. سيبها فاضية لو مش عايز لينك.",
                en: "Where the photo links. Leave empty for no link.",
            },
            showIf: { variant: ["look-classic"] },
        },
        {
            id: "productIds",
            type: "productPicker",
            maxItems: 6,
            label: { ar: "منتجات الإطلالة", en: "Products in this look" },
            helpText: {
                ar: "اختار المنتجات اللي ظاهرة في الصورة.",
                en: "Pick the products shown in the photo.",
            },
            showIf: { variant: ["look-classic"] },
        },
        ...colorFields(),
    ],
    blocks: [
        {
            /**
             * `look-carousel` only — one slide of the scroller.
             *
             * The classic layout's single look lives in the SETTINGS above
             * (`heroImage` + `productIds`) because there is exactly one of it, and
             * moving it into blocks would have rewritten every stored avyro/elite
             * section. So the two layouts read two different places, and the
             * `showIf`s above keep the inspector honest about which.
             */
            type: "look",
            name: { ar: "إطلالة", en: "Look" },
            description: {
                ar: "صورة إطلالة والمنتجات اللي فيها.",
                en: "One look photo and the products inside it.",
            },
            // MEASURED: their track carries 4. 8 leaves room without turning the
            // scroller into a gallery.
            maxPerSection: 8,
            settings: [
                {
                    id: "image",
                    type: "image",
                    label: { ar: "صورة الإطلالة", en: "Look photo" },
                    helpText: {
                        ar: "الصورة بتتعرض بارتفاع ثابت وعرضها بيتبع نسبتها الأصلية.",
                        en: "Shown at a fixed height; its width follows the photo's own ratio.",
                    },
                    focalPoint: true,
                },
                {
                    id: "label",
                    type: "text",
                    i18n: true,
                    label: { ar: "اسم الإطلالة", en: "Look name" },
                    helpText: {
                        ar: "بيتقري لقارئ الشاشة وبيظهر في نافذة المنتجات.",
                        en: "Read by screen readers and shown at the top of the products dialog.",
                    },
                    validate: { max: 60 },
                },
                {
                    id: "productIds",
                    type: "productPicker",
                    maxItems: 8,
                    label: { ar: "منتجات الإطلالة", en: "Products in this look" },
                    helpText: {
                        ar: "بتظهر في نافذة لما الزائر يدوس على الشارة.",
                        en: "Shown in a dialog when the shopper opens the slide's badge.",
                    },
                },
            ],
        },
    ],
};
//# sourceMappingURL=shop-the-look.js.map